import { useState } from 'react';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/state/store';
import { useScanDetails } from '@/components/smartai/api/getScanDetails';
import type { ScanDetail } from '@/components/smartai/api/getScanDetails';
import {
  useCompareCoverageBuild,
  useCompareCoverageTimeline,
} from '@/components/smartai/api/compareCoverage';
import type {
  CoverageCompareData,
  CoverageSummary,
  EntityTypeBreakdown,
  FileLevelComparisonItem,
} from '@/components/smartai/api/compareCoverage';
import CodeEditor from '@/components/common/CodeEditor';
import { useSelectedComponent } from '@/hooks/useSelectedComponent';
import ComponentNotFound from '@/hooks/ComponentNotFound';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import OnboardingProjectHeader from '@/components/smartai/ComponentOnboarding/OnboardingProjectHeader';
import { formLabelClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';
import { Combobox } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import TestGapSkeleton from './TestGapSkeleton';
import { Badge } from '@/components/ui/badge';
import { OnboardingTable } from '@/components/ui/onboarding-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  BreakdownTile,
  BuildDiffCardHeader,
  CompactProgressBar,
  CoverageModeToggle,
  ModeToggle,
  ScanDetailsPanel,
  testGapCardClass,
  testGapBreakdownDetailGridClass,
  testGapBreakdownGridClass,
  testGapPageClass,
  testGapResultsSectionClass,
  testGapSectionTitleClass,
  testGapStatsGridClass,
  testGapSubsectionTitleClass,
  TgaStatCard,
} from './test-gap-layout';

const TODAY = new Date().toISOString().slice(0, 10);

type CoverageMode = 'lines' | 'inventory';

interface BreakdownItem {
  type: string;
  count: number;
  covered: number;
  coverage: number;
}

interface TransformedFile {
  path: string;
  coverage: number;
  total: number;
  covered: number;
  uncovered: number;
  status: string;
  fileName?: string;
  completeFile: FileLevelComparisonItem;
}

interface TransformedSummary {
  total: { percentage: number; covered: number; total: number };
  uncovered: { percentage: number; count: number };
  addedLines: number;
  modifiedLines: number;
  deletedLines: number;
  addedEle: number;
  modifiedEle: number;
  deletedEle: number;
}

const formatScanLabel = (scan: ScanDetail): string =>
  `#${scan.buildNumber} · ${(scan.branchName || 'main').replace(/^refs\/heads\//, '')}`;

const ComponentTestIntelligenceView = () => {
  const { ready, isLoading, notFound } = useSelectedComponent();
  const userSelectedComponent = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );

  // Fetch scan/build list
  const { data: scanData, isPending: isScansLoading } = useScanDetails({
    repoId: userSelectedComponent.dbRepoId,
    branchId: userSelectedComponent.branchId,
  });

  const scans: ScanDetail[] = (scanData?.data ?? []).filter(s => s.buildId);

  // Mutations for coverage comparison
  const { mutate: compareBuild, isPending: isComparingBuild } =
    useCompareCoverageBuild(userSelectedComponent.pat);
  const { mutate: compareTimeline, isPending: isComparingTimeline } =
    useCompareCoverageTimeline(userSelectedComponent.pat);

  const isComparing = isComparingBuild || isComparingTimeline;

  // Local state
  const [buildBOptions, setBuildBOptions] = useState<ScanDetail[]>([]);
  const [selectedBuildA, setSelectedBuildA] = useState('');
  const [selectedBuildB, setSelectedBuildB] = useState('');
  const [buildAObj, setBuildAObj] = useState<ScanDetail | undefined>();
  const [buildBObj, setBuildBObj] = useState<ScanDetail | undefined>();

  const [dateSwitch, setDateSwitch] = useState(true);
  const [dateA, setDateA] = useState('');
  const [dateB, setDateB] = useState('');

  const [coverageData, setCoverageData] = useState<CoverageCompareData | null>(
    null,
  );
  const [coverageMode, setCoverageMode] = useState<CoverageMode>('lines');

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [codeDetails, setCodeDetails] =
    useState<FileLevelComparisonItem | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleBuildAChange = (val: string | number | (string | number)[]) => {
    const scanRef = String(Array.isArray(val) ? val[0] : val);
    const buildA = scans.find(s => s.scanRef === scanRef);
    setBuildAObj(buildA);
    setSelectedBuildA(scanRef);
    setSelectedBuildB('');
    setBuildBObj(undefined);

    if (buildA) {
      const compareDate = new Date(buildA.completedAt);
      setBuildBOptions(
        scans.filter(s => new Date(s.completedAt) > compareDate),
      );
    }
  };

  const handleBuildBChange = (val: string | number | (string | number)[]) => {
    const scanRef = String(Array.isArray(val) ? val[0] : val);
    const buildB = scans.find(s => s.scanRef === scanRef);
    setBuildBObj(buildB);
    setSelectedBuildB(scanRef);
  };

  const handleGenerateReport = () => {
    console.log(`${new Date(dateB).toLocaleDateString('en-CA')}`);
    if (!dateSwitch) {
      // Compare by build selection
      if (!selectedBuildA || !selectedBuildB) return;
      compareBuild(
        { scanId1: selectedBuildA, scanId2: selectedBuildB },
        { onSuccess: res => setCoverageData(res.data) },
      );
    } else {
      // Compare by date range
      if (!dateA || !dateB) return;
      compareTimeline(
        {
          repoId: userSelectedComponent.dbRepoId,
          date1: new Date(dateA).toLocaleDateString('en-CA'),
          date2: new Date(dateB).toLocaleDateString('en-CA'),
        },
        { onSuccess: res => setCoverageData(res.data) },
      );
    }
  };

  // ── Data transformers ───────────────────────────────────────────────────

  const transformSummary = (summary: CoverageSummary): TransformedSummary => {
    const linesMode = summary.linesOfCodeMode;
    const invMode = summary.inventoryMode;

    const covered =
      coverageMode === 'lines'
        ? linesMode.coveredChangedLines
        : invMode.coveredChangedElements;

    const total =
      coverageMode === 'lines'
        ? linesMode.totalChangedLines
        : invMode.totalChangedElements;

    const notCovered =
      coverageMode === 'lines'
        ? linesMode.notCoveredLines
        : invMode.notCoveredElements;

    const percentage =
      coverageMode === 'lines' ? linesMode.percentage : invMode.percentage;
    const notCoveredPct = total > 0 ? (notCovered / total) * 100 : 0;

    return {
      total: { percentage, covered, total },
      uncovered: { percentage: notCoveredPct, count: notCovered },
      addedLines: summary.linesChangeByStatus.added,
      modifiedLines: summary.linesChangeByStatus.modified,
      deletedLines: summary.linesChangeByStatus.deleted,
      addedEle: summary.inventoryChangeByStatus.added,
      modifiedEle: summary.inventoryChangeByStatus.modified,
      deletedEle: summary.inventoryChangeByStatus.deleted,
    };
  };

  const transformBreakdown = (
    entityBreakdown: EntityTypeBreakdown,
  ): BreakdownItem[] =>
    Object.entries(entityBreakdown).map(([key, val]) => ({
      type: key.charAt(0).toUpperCase() + key.slice(1),
      count: val.totalChanged,
      covered: val.coveredChanged,
      coverage: val.percentage,
    }));

  const transformFiles = (
    files: FileLevelComparisonItem[],
  ): TransformedFile[] =>
    files.map(file => {
      if (coverageMode === 'lines') {
        const loc = file.linesOfCodeMode;
        return {
          path: file.filePath,
          coverage: loc?.percentage ?? 0,
          total: loc?.totalChangedLines ?? 0,
          covered: loc?.coveredChangedLines ?? 0,
          uncovered: loc?.notCoveredLines ?? 0,
          status: file.status,
          fileName: file.fileName,
          completeFile: file,
        };
      }
      const inv = file.inventoryMode;
      return {
        path: file.filePath,
        coverage: inv?.percentage ?? 0,
        total: inv?.totalChangedElements ?? 0,
        covered: inv?.coveredChangedElements ?? 0,
        uncovered: inv?.notCoveredElements ?? 0,
        status: file.status,
        fileName: file.fileName,
        completeFile: file,
      };
    });

  const summary = coverageData?.summary;
  const data = summary ? transformSummary(summary) : null;
  const breakdown =
    coverageMode === 'inventory' && summary?.entityTypeBreakdown
      ? transformBreakdown(summary.entityTypeBreakdown)
      : [];
  const methodCard = summary?.entityTypeBreakdown
    ? transformBreakdown(summary.entityTypeBreakdown).find(
        b => b.type === 'Methods',
      )
    : undefined;
  const methodClass = breakdown.filter(
    item => item.type === 'Methods' || item.type === 'Classes',
  );
  const remainingEle = breakdown.filter(
    item => item.type !== 'Methods' && item.type !== 'Classes',
  );
  const files = coverageData?.fileLevelComparison
    ? transformFiles(coverageData.fileLevelComparison)
    : [];
  const unitLabel = coverageMode === 'lines' ? 'lines' : 'elements';

  const handleViewCode = (file: TransformedFile) => {
    setCodeDetails(file.completeFile);
    setShowDetailModal(true);
  };

  if (isLoading || (!ready && !notFound) || isScansLoading) {
    return <TestGapSkeleton />;
  }
  if (notFound) return <ComponentNotFound />;

  return (
    <div className={testGapPageClass}>
      <OnboardingPageHeader
        eyebrow="Coverage Analysis"
        title="Test Gap Analysis"
        subtitle="Track repository movements and compare coverage between builds."
      />

      <OnboardingProjectHeader
        org={userSelectedComponent.orgName}
        project={userSelectedComponent.projectName || '—'}
        repoName={userSelectedComponent.repoName || '—'}
        branchName={
          (userSelectedComponent.branchName || 'main').replace(
            /^refs\/heads\//,
            '',
          ) || '—'
        }
      />

      <section className={testGapCardClass} aria-label="Compare coverage">
        <BuildDiffCardHeader
          className="mb-3 2xl:mb-4"
          title="Compare"
          subtitle={
            dateSwitch
              ? 'Select a date range to compare coverage over time.'
              : 'Select two scanned builds to compare coverage.'
          }
          action={<ModeToggle byDate={dateSwitch} onChange={setDateSwitch} />}
        />

        {!dateSwitch ? (
          <div className="flex flex-col gap-3 2xl:gap-4">
            <div className="hidden grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-4 gap-y-2 min-[769px]:grid 2xl:gap-x-5">
              <label className={formLabelClass} htmlFor="scan-a-select">
                Build A (from)
              </label>
              <span aria-hidden />
              <label className={formLabelClass} htmlFor="scan-b-select">
                Build B (to)
              </label>
              <Combobox
                id="scan-a-select"
                data={scans.map(s => ({
                  value: s.scanRef,
                  label: formatScanLabel(s),
                }))}
                placeholder="Select earlier build"
                value={selectedBuildA || null}
                onSelect={handleBuildAChange}
                onClear={() => {
                  setSelectedBuildA('');
                  setBuildAObj(undefined);
                  setSelectedBuildB('');
                  setBuildBObj(undefined);
                  setBuildBOptions([]);
                }}
                showClear
              />
              <div className="flex h-10 items-center justify-center px-1" aria-hidden>
                <ArrowRight className="h-[1.35rem] w-[1.35rem] shrink-0 text-[var(--inv-accent)]" />
              </div>
              <Combobox
                id="scan-b-select"
                data={buildBOptions.map(s => ({
                  value: s.scanRef,
                  label: formatScanLabel(s),
                }))}
                placeholder="Select later build"
                value={selectedBuildB || null}
                onSelect={handleBuildBChange}
                onClear={() => {
                  setSelectedBuildB('');
                  setBuildBObj(undefined);
                }}
                showClear
                disabled={!selectedBuildA}
              />
            </div>

            <div className="flex flex-col gap-3 2xl:gap-4 min-[769px]:hidden">
              <div className="min-w-0">
                <label className={formLabelClass} htmlFor="scan-a-select-mobile">
                  Build A (from)
                </label>
                <Combobox
                  id="scan-a-select-mobile"
                  data={scans.map(s => ({
                    value: s.scanRef,
                    label: formatScanLabel(s),
                  }))}
                  placeholder="Select earlier build"
                  value={selectedBuildA || null}
                  onSelect={handleBuildAChange}
                  onClear={() => {
                    setSelectedBuildA('');
                    setBuildAObj(undefined);
                    setSelectedBuildB('');
                    setBuildBObj(undefined);
                    setBuildBOptions([]);
                  }}
                  showClear
                />
              </div>
              <div className="flex justify-center py-1" aria-hidden>
                <ArrowRight className="h-[1.35rem] w-[1.35rem] rotate-90 text-[var(--inv-accent)]" />
              </div>
              <div className="min-w-0">
                <label className={formLabelClass} htmlFor="scan-b-select-mobile">
                  Build B (to)
                </label>
                <Combobox
                  id="scan-b-select-mobile"
                  data={buildBOptions.map(s => ({
                    value: s.scanRef,
                    label: formatScanLabel(s),
                  }))}
                  placeholder="Select later build"
                  value={selectedBuildB || null}
                  onSelect={handleBuildBChange}
                  onClear={() => {
                    setSelectedBuildB('');
                    setBuildBObj(undefined);
                  }}
                  showClear
                  disabled={!selectedBuildA}
                />
              </div>
            </div>

            {(selectedBuildA && buildAObj) || (selectedBuildB && buildBObj) ? (
              <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-x-4 gap-y-3 max-[768px]:grid-cols-1 2xl:gap-x-5 2xl:gap-y-4">
                <div className="min-w-0">
                  {selectedBuildA && buildAObj ? (
                    <ScanDetailsPanel scan={buildAObj} />
                  ) : null}
                </div>
                <span className="max-[768px]:hidden" aria-hidden />
                <div className="min-w-0">
                  {selectedBuildB && buildBObj ? (
                    <ScanDetailsPanel scan={buildBObj} />
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3 2xl:gap-4">
            <div className="hidden grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-4 gap-y-2 min-[769px]:grid 2xl:gap-x-5">
              <label className={formLabelClass} htmlFor="date-a">
                Date A (from)
              </label>
              <span aria-hidden />
              <label className={formLabelClass} htmlFor="date-b">
                Date B (to)
              </label>
              <Input
                id="date-a"
                type="date"
                value={dateA}
                min="2025-01-01"
                max={TODAY}
                onChange={e => setDateA(e.target.value)}
              />
              <div className="flex h-10 items-center justify-center px-1" aria-hidden>
                <ArrowRight className="h-[1.35rem] w-[1.35rem] shrink-0 text-[var(--inv-accent)]" />
              </div>
              <Input
                id="date-b"
                type="date"
                value={dateB}
                min={dateA || '2025-01-01'}
                max={TODAY}
                onChange={e => setDateB(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-3 2xl:gap-4 min-[769px]:hidden">
              <div className="min-w-0">
                <label className={formLabelClass} htmlFor="date-a-mobile">
                  Date A (from)
                </label>
                <Input
                  id="date-a-mobile"
                  type="date"
                  value={dateA}
                  min="2025-01-01"
                  max={TODAY}
                  onChange={e => setDateA(e.target.value)}
                />
              </div>
              <div className="flex justify-center py-1" aria-hidden>
                <ArrowRight className="h-[1.35rem] w-[1.35rem] rotate-90 text-[var(--inv-accent)]" />
              </div>
              <div className="min-w-0">
                <label className={formLabelClass} htmlFor="date-b-mobile">
                  Date B (to)
                </label>
                <Input
                  id="date-b-mobile"
                  type="date"
                  value={dateB}
                  min={dateA || '2025-01-01'}
                  max={TODAY}
                  onChange={e => setDateB(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-end border-t border-[var(--inv-border-soft)] pt-3 2xl:mt-5 2xl:pt-4">
          <Button
            onClick={handleGenerateReport}
            disabled={
              isComparing ||
              (!dateSwitch
                ? !selectedBuildA || !selectedBuildB
                : !dateA || !dateB)
            }>
            {isComparing
              ? 'Generating report…'
              : 'Generate comparison report'}
          </Button>
        </div>
      </section>

      {coverageData && data && summary ? (
        <section
          className={testGapResultsSectionClass}
          aria-label="Coverage comparison results">
          <h2 className={testGapSectionTitleClass}>
            Coverage comparison results: Build{' '}
            {coverageData.scanA?.buildNumber ?? 'A'} → Build{' '}
            {coverageData.scanB?.buildNumber ?? 'B'}
          </h2>

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 2xl:mb-5 2xl:gap-4">
            <div>
              <h3 className="m-0 text-[0.875rem] font-semibold text-[var(--inv-text)] 2xl:text-[0.95rem]">
                Coverage calculation mode
              </h3>
              <p className="m-0 mt-0.5 text-[0.75rem] text-[var(--inv-text-secondary)] 2xl:mt-1 2xl:text-[0.8125rem]">
                Choose how coverage is measured and displayed.
              </p>
            </div>
            <CoverageModeToggle mode={coverageMode} onChange={setCoverageMode} />
          </div>

          <div className="mb-4 rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] px-3 py-2.5 text-[0.75rem] leading-relaxed text-[var(--inv-text-secondary)] 2xl:mb-5 2xl:px-4 2xl:py-3 2xl:text-[0.8125rem]">
            {coverageMode === 'lines' ? (
              <p className="m-0">
                <strong className="text-[var(--inv-text)]">
                  Lines of code mode:
                </strong>{' '}
                Measures coverage based on changed executable lines of code (
                {data.total.covered.toLocaleString()} /{' '}
                {data.total.total.toLocaleString()} lines ={' '}
                {data.total.percentage.toFixed(2)}%).
              </p>
            ) : (
              <p className="m-0">
                <strong className="text-[var(--inv-text)]">Inventory mode:</strong>{' '}
                Measures coverage based on changed code elements (
                {data.total.covered} / {data.total.total} elements ={' '}
                {data.total.percentage.toFixed(2)}%).
              </p>
            )}
          </div>

          <div className={testGapStatsGridClass}>
            <TgaStatCard
              title="Changed code coverage"
              value={`${data.total.percentage.toFixed(2)}%`}
              progress={{ value: data.total.percentage, tone: 'accent' }}
              sub={
                <>
                  {data.total.covered.toLocaleString()} /{' '}
                  {data.total.total.toLocaleString()} {unitLabel} covered
                </>
              }
            />
            <TgaStatCard
              title={
                coverageMode === 'lines'
                  ? 'Total changed lines'
                  : 'Total changed elements'
              }
              value={data.total.total.toLocaleString()}
              sub={
                coverageMode === 'lines' ? (
                  <>
                    Added {data.addedLines} + modified {data.modifiedLines} |
                    deleted {data.deletedLines}
                  </>
                ) : (
                  <>
                    Added {data.addedEle} + modified {data.modifiedEle} |
                    deleted {data.deletedEle}
                  </>
                )
              }
            />
            {methodCard ? (
              <TgaStatCard
                title="Methods covered"
                value={`${methodCard.covered}/${methodCard.count}`}
                sub={`${methodCard.coverage.toFixed(2)}% covered`}
              />
            ) : null}
            <TgaStatCard
              title="Files changed"
              value={summary.filesChanged}
              sub="Files modified between the two builds"
            />
            <TgaStatCard
              title="Not covered"
              value={`${data.uncovered.percentage.toFixed(2)}%`}
              progress={{ value: data.uncovered.percentage, tone: 'danger' }}
              sub={
                <>
                  {data.uncovered.count.toLocaleString()} /{' '}
                  {data.total.total.toLocaleString()} {unitLabel} not covered
                </>
              }
            />
            {methodCard ? (
              <TgaStatCard
                title="Uncovered methods"
                value={methodCard.count - methodCard.covered}
                sub="Methods without test coverage in the diff"
              />
            ) : null}
          </div>

          {coverageMode === 'inventory' && breakdown.length > 0 ? (
            <div className="mb-4 2xl:mb-6">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 2xl:mb-4 2xl:gap-3">
                <h3 className={testGapSubsectionTitleClass}>
                  Coverage by code element type (changed)
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDetails(v => !v)}>
                  {showDetails ? 'Hide details' : 'Show details'}
                </Button>
              </div>

              <div className={testGapBreakdownGridClass}>
                {methodClass.map(item => (
                  <BreakdownTile
                    key={item.type}
                    label={item.type}
                    covered={item.covered}
                    count={item.count}
                    coverage={item.coverage}
                  />
                ))}
              </div>

              {showDetails ? (
                <div className={testGapBreakdownDetailGridClass}>
                  {remainingEle.map(item => (
                    <BreakdownTile
                      key={item.type}
                      label={item.type}
                      covered={item.covered}
                      count={item.count}
                      coverage={item.coverage}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {files.length > 0 ? (
            <div>
              <h3 className={cn(testGapSubsectionTitleClass, 'mb-3 2xl:mb-4')}>
                Changed file coverage breakdown
              </h3>
              <OnboardingTable
                data={files}
                rowKey={row => row.path}
                columns={[
                  {
                    key: 'path',
                    header: 'File path',
                    render: row => (
                      <span className="break-all font-mono text-[0.8125rem] text-[var(--inv-text)]">
                        {row.path}
                      </span>
                    ),
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: row => (
                      <Badge variant="tag" className="capitalize">
                        {row.status}
                      </Badge>
                    ),
                  },
                  {
                    key: 'coverage',
                    header: 'Coverage %',
                    render: row => (
                      <div className="flex items-center gap-2">
                        <CompactProgressBar
                          value={row.coverage}
                          className="max-w-[5rem]"
                        />
                        <span className="text-[0.8125rem] font-medium tabular-nums">
                          {row.coverage.toFixed(2)}%
                        </span>
                      </div>
                    ),
                  },
                  {
                    key: 'total',
                    header:
                      coverageMode === 'lines'
                        ? 'Total changed lines'
                        : 'Total changed elements',
                    render: row => (
                      <span className="tabular-nums">
                        {row.total.toLocaleString()}
                      </span>
                    ),
                  },
                  {
                    key: 'covered',
                    header: 'Covered',
                    render: row => (
                      <span className="tabular-nums text-[#16a34a]">
                        {row.covered.toLocaleString()}
                      </span>
                    ),
                  },
                  {
                    key: 'uncovered',
                    header: 'Not covered',
                    render: row => (
                      <span className="tabular-nums text-[#d92d20]">
                        {row.uncovered.toLocaleString()}
                      </span>
                    ),
                  },
                  {
                    key: 'actions',
                    header: '',
                    className: 'w-12 text-right',
                    render: row => (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        aria-label={`View code for ${row.path}`}
                        onClick={() => handleViewCode(row)}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    ),
                  },
                ]}
              />
            </div>
          ) : null}
        </section>
      ) : null}

      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-h-[90vh] w-[92vw] max-w-[min(92vw,72rem)] overflow-y-auto p-6">
          <DialogHeader>
            <DialogTitle className="break-all pr-8">
              {codeDetails?.fileName || codeDetails?.filePath || 'File diff'}
            </DialogTitle>
          </DialogHeader>
          {codeDetails ? (
            <div
              className={cn(
                'grid gap-4',
                codeDetails.status === 'added'
                  ? 'grid-cols-1'
                  : 'grid-cols-1 min-[900px]:grid-cols-2',
              )}>
              {codeDetails.status !== 'added' ? (
                <div className="min-w-0">
                  <p className="mb-2 text-[0.8125rem] font-semibold text-[var(--inv-text-secondary)]">
                    Build A: {coverageData?.scanA?.buildNumber}
                  </p>
                  <CodeEditor
                    instanceId={`tga-buildA-${codeDetails.filePath}`}
                    codeFromApi={codeDetails.oldContent ?? ''}
                    language={codeDetails.filePath}
                    deletions={codeDetails.deletedLineNumbers ?? []}
                    nonExecutable={
                      codeDetails.totalNonExecutableLineNumbersInFile ?? []
                    }
                  />
                </div>
              ) : null}
              <div className="min-w-0">
                <p className="mb-2 text-[0.8125rem] font-semibold text-[var(--inv-text-secondary)]">
                  Build B: {coverageData?.scanB?.buildNumber}
                </p>
                <CodeEditor
                  instanceId={`tga-buildB-${codeDetails.filePath}`}
                  codeFromApi={codeDetails.newContent ?? ''}
                  language={codeDetails.filePath}
                  additions={codeDetails.addedLineNumbers ?? []}
                  modified={(codeDetails.modifiedLineNumbers ?? []).map(
                    ([, newLine]) => newLine,
                  )}
                  executable={codeDetails.totalExecutableLineNumbersInFile ?? []}
                  nonExecutable={
                    codeDetails.totalNonExecutableLineNumbersInFile ?? []
                  }
                  covered={
                    codeDetails.linesOfCodeMode?.changedCoveredLineNumbers ?? []
                  }
                  uncovered={
                    codeDetails.linesOfCodeMode
                      ?.notCoveredChangedExecutableLineNumbers ?? []
                  }
                />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComponentTestIntelligenceView;
